import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useI18n } from "../i18n";
import { Menu, X } from "lucide-react";

const Logo = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
    <div className="relative h-8 w-8 flex items-center justify-center">
      <div className="absolute inset-0 bg-[#00B3B3] opacity-80 group-hover:opacity-100 transition-opacity" style={{ clipPath: "polygon(0 0, 100% 0, 70% 100%, 0 100%)" }} />
      <span className="relative font-heading font-black text-black text-sm">B</span>
    </div>
    <div className="font-heading font-black tracking-tight text-white text-lg leading-none">
      BEYOND<span className="text-[#00B3B3]">CALCULUS</span>
    </div>
  </Link>
);

const LangSwitcher = () => {
  const { lang, setLang } = useI18n();
  return (
    <div className="flex items-center gap-1 font-mono text-xs tracking-widest border border-white/15 rounded-sm overflow-hidden">
      <button
        data-testid="lang-pt-btn"
        onClick={() => setLang("pt")}
        className={`px-3 py-1.5 transition-colors ${lang === "pt" ? "bg-[#00B3B3] text-white" : "text-white/70 hover:text-white"}`}
      >PT</button>
      <button
        data-testid="lang-en-btn"
        onClick={() => setLang("en")}
        className={`px-3 py-1.5 transition-colors ${lang === "en" ? "bg-[#00B3B3] text-white" : "text-white/70 hover:text-white"}`}
      >EN</button>
    </div>
  );
};

export default function Navbar() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const links = [
    { to: "/", label: t.nav.home, key: "home" },
    { to: "/services", label: t.nav.services, key: "services" },
    { to: "/solutions", label: t.nav.solutions, key: "solutions" },
    { to: "/about", label: t.nav.about, key: "about" },
    { to: "/contact", label: t.nav.contact, key: "contact" },
  ];

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-16 flex items-center justify-between">
        <Logo />
        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.key}
              to={l.to}
              data-testid={`nav-${l.key}-link`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${isActive ? "text-white" : "text-white/60 hover:text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>
        <div className="hidden lg:flex items-center gap-4">
          <LangSwitcher />
          <Link
            to="/contact"
            data-testid="nav-cta-btn"
            className="bg-[#00B3B3] text-white font-semibold text-sm px-4 py-2 rounded-sm hover:bg-[#00CCCC] transition-colors"
          >
            {t.nav.cta}
          </Link>
        </div>
        <button
          className="lg:hidden text-white p-2"
          data-testid="mobile-menu-btn"
          onClick={() => setOpen((s) => !s)}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>
      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black/95">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.key}-link`}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-[#00B3B3]" : "text-white/80"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <LangSwitcher />
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-cta-btn"
                className="bg-[#00B3B3] text-white font-semibold text-sm px-4 py-2 rounded-sm"
              >
                {t.nav.cta}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
